import { Suspense } from 'react';
import { useRecoilValue } from 'recoil';

import type * as api from '@gitbook/api-client';
import { useViewport } from '@gitbook/util-react-hooks';

import { siteVisitorSegmentSelector } from '../AdaptiveContent';
import type { SitePreviewContent } from './getSiteURLForContent';
import { PreviewDeviceFrame } from './PreviewDeviceFrame';
import { SitePreviewIframe } from './SitePreviewIframe';
import { SitePreviewToolbar } from './SitePreviewToolbar';
import { sitePreviewAtom } from './state';

interface SitePreviewProps {
    organization: api.Organization;
    site: api.Site;

    /** Content to load in the preview */
    content?: SitePreviewContent;

    /** Canonical content to present in the toolbar */
    canonicalContent?: SitePreviewContent;

    /** Indicates that the preview is being loaded and not ready to be shown */
    loading?: boolean;
}

/**
 * Screen to preview a site.
 */
export function SitePreviewScreen(props: SitePreviewProps) {
    const { site, organization, content = {}, canonicalContent = content, loading = false } = props;
    const previewOptions = useRecoilValue(sitePreviewAtom);
    const visitorSegment = useRecoilValue(siteVisitorSegmentSelector(site.id));
    const screenDimensions = useViewport();

    const iframe = (
        <SitePreviewIframe
            site={site}
            organization={organization}
            content={content}
            claims={site.adaptiveContent?.enabled ? (visitorSegment?.claims ?? {}) : undefined}
            className="h-full w-full"
            fallback="text"
            interactive
        />
    );

    const fallback = <div className="shimmer h-full w-full flex-1" aria-busy />;

    return (
        <div className="flex flex-1 flex-col">
            <SitePreviewToolbar
                site={site}
                organizationId={organization.id}
                content={canonicalContent}
            />
            <div className="flex flex-1 flex-col items-center justify-center bg-muted">
                {loading ? (
                    fallback
                ) : (
                    <Suspense fallback={fallback}>
                        {previewOptions.device === 'desktop' ? (
                            iframe
                        ) : (
                            <PreviewDeviceFrame
                                device={previewOptions.device}
                                frame={{
                                    maxWidth: screenDimensions.width - 300,
                                    maxHeight: screenDimensions.height - 120,
                                }}
                            >
                                {iframe}
                            </PreviewDeviceFrame>
                        )}
                    </Suspense>
                )}
            </div>
        </div>
    );
}
